import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { TextField } from '../../../../components/inputs/TextField';
import { Textarea } from '../../../../components/inputs/Textarea';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { rolesApi } from '../rolesApi';
import type { RoleListItem } from '../roleTypes';
import { sampleRole } from '../utils/sampleRole';

/**
 * Edit what a role IS — its name and description.
 *
 * **What it GRANTS is not on this form**, because `PUT /api/roles/{id}` writes neither and
 * the permissions carry their own key (`roles.assign-permissions`). Folding the grid in here
 * would make one form need two keys, and a reader holding `roles.update` alone would open it
 * onto a picker they cannot save.
 */
export function EditRoleModal({
  role,
  onClose,
  onSaved,
}: {
  /** The parent mounts this only while a role is being edited, so the fields seed once. */
  role: Pick<RoleListItem, 'id' | 'name' | 'description'>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description ?? '');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const valid = name.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await rolesApi.update(role.id, {
        name: name.trim(),
        description: description.trim() || null,
      });

      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save this role.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'edit-role' });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    const sample = sampleRole();

    setName(sample.name);
    setDescription(sample.description);
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Edit role"
      size="lg"
      id="edit-role-modal"
      autogenerate={{ id: 'edit-role-autogenerate', onGenerate: generate, disabled: saving }}
      confirm={{
        id: 'edit-role-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="edit-role-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>

          <Button
            id="edit-role-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <TextField
          id="edit-role-name"
          label="Name"
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
          error={name.trim() ? error?.fieldError('name') : 'Enter a name.'}
          description="Letters, digits, spaces, hyphens and underscores only. It must not match an existing role."
          disabled={saving}
          maxLength={100}
        />

        {/* A free note about the record as a whole — full width, with the ceiling `<Textarea>` requires. */}
        <Textarea
          id="edit-role-description"
          label="Description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          error={error?.fieldError('description')}
          description="What this role is for. Shown beside it wherever the role is listed."
          disabled={saving}
          maxLength={500}
          minRows={2}
          maxRows={6}
        />

        <p className="text-xs text-slate-500">
          Every account holding this role keeps it under the new name. What the role grants is
          unchanged — that is the Permissions tab's own edit.
        </p>
      </div>
    </Modal>
  );
}
